import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { HelpCircle, Loader2, Pencil, Plus, RefreshCw, Save, Search, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const emptyForm = { id: '', question: '', answer: '', type: '', source: '', tags: '', metadata: {} };
const textAreaClass = 'w-full resize-y rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface-raised)] px-3 py-2 text-sm text-[var(--tg-text-primary)] outline-none focus:border-indigo-400';
const errorText = (error, fallback) => error?.response?.data?.detail || error?.message || fallback;
const rowsFrom = (payload) => Array.isArray(payload) ? payload : (Array.isArray(payload?.data) ? payload.data : []);

const FaqKnowledgePanel = ({ apiBase, authConfig, enabled = true, defaultAgentId = '' }) => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [search, setSearch] = useState('');
  const [agentId, setAgentId] = useState(defaultAgentId);
  const [appliedAgentId, setAppliedAgentId] = useState(defaultAgentId);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async () => {
    if (!enabled) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const query = appliedAgentId.trim() ? `?agent_id=${encodeURIComponent(appliedAgentId.trim())}` : '';
      const response = await axios.get(`${apiBase}/knowledge/faqs${query}`, authConfig());
      setItems(rowsFrom(response.data));
    } catch (loadError) {
      if (loadError.response?.status === 404) setItems([]);
      else setError(errorText(loadError, 'Could not load FAQs from Meta.'));
    } finally {
      setLoading(false);
    }
  }, [appliedAgentId, apiBase, authConfig, enabled]);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((item) => `${item.question || ''} ${item.answer || ''}`.toLowerCase().includes(needle));
  }, [items, search]);

  const startEdit = (item) => {
    const metadata = item.metadata && typeof item.metadata === 'object' ? item.metadata : {};
    setForm({
      id: item.id,
      question: item.question || '',
      answer: item.answer || '',
      type: metadata.type || '',
      source: metadata.source || '',
      tags: Array.isArray(metadata.tags) ? metadata.tags.join(', ') : (metadata.tags || ''),
      metadata,
    });
  };

  const save = async () => {
    if (!form.question.trim() || !form.answer.trim()) return;
    setWorking(true);
    setError('');
    setNotice('');
    const tags = form.tags.split(',').map((tag) => tag.trim()).filter(Boolean);
    const metadata = { ...form.metadata };
    if (form.type.trim()) metadata.type = form.type.trim(); else delete metadata.type;
    if (form.source.trim()) metadata.source = form.source.trim(); else delete metadata.source;
    if (tags.length) metadata.tags = tags; else delete metadata.tags;
    const payload = { question: form.question.trim(), answer: form.answer.trim(), metadata };
    try {
      if (form.id) {
        await axios.put(`${apiBase}/knowledge/faqs/${encodeURIComponent(form.id)}`, payload, authConfig());
        setNotice('FAQ updated in Meta.');
      } else {
        const query = appliedAgentId.trim() ? `?agent_id=${encodeURIComponent(appliedAgentId.trim())}` : '';
        await axios.post(`${apiBase}/knowledge/faqs${query}`, payload, authConfig());
        setNotice('FAQ created in Meta.');
      }
      setForm(emptyForm);
      await load();
    } catch (saveError) {
      setError(errorText(saveError, 'Could not save the FAQ.'));
    } finally {
      setWorking(false);
    }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete the FAQ “${item.question}”?`)) return;
    setWorking(true);
    setError('');
    setNotice('');
    try {
      await axios.delete(`${apiBase}/knowledge/faqs/${encodeURIComponent(item.id)}`, authConfig());
      if (form.id === item.id) setForm(emptyForm);
      setNotice('FAQ deleted from Meta.');
      await load();
    } catch (deleteError) {
      setError(errorText(deleteError, 'Could not delete the FAQ.'));
    } finally {
      setWorking(false);
    }
  };

  if (!enabled) return <div className="rounded-2xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300">Configure the WhatsApp number and server token before managing Agent Knowledge.</div>;

  return (
    <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(320px,0.75fr)]">
      <section className="rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-5 sm:p-6">
        <div className="flex flex-wrap items-start justify-between gap-3"><div className="flex gap-3"><div className="rounded-xl bg-indigo-500/15 p-2.5 text-indigo-400"><HelpCircle className="h-5 w-5" /></div><div><h2 className="text-xl font-semibold">FAQs</h2><p className="text-sm text-[var(--tg-text-muted)]">Keep answers concise and focused on one customer question.</p></div></div><Button variant="outline" onClick={load} disabled={loading || working}><RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />Refresh</Button></div>
        <div data-tour="faq-search" className="mt-5 grid gap-3 sm:grid-cols-[1fr_auto]">
          <label className="relative"><Search className="absolute left-3 top-2.5 h-4 w-4 text-[var(--tg-text-muted)]" /><Input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search FAQs" className="pl-9" /></label>
          <Button onClick={() => setForm(emptyForm)}><Plus className="mr-2 h-4 w-4" />New FAQ</Button>
        </div>
        <details data-tour="faq-agent-scope" className="mt-3 rounded-xl border border-[var(--tg-border-soft)] px-3 py-2"><summary className="cursor-pointer text-xs font-medium text-[var(--tg-text-secondary)]">Advanced: scope to an Agent ID</summary><div className="mt-3 flex gap-2"><Input value={agentId} onChange={(event) => setAgentId(event.target.value)} placeholder="Optional agent ID" /><Button variant="outline" onClick={() => setAppliedAgentId(agentId.trim())}>Apply</Button></div></details>
        {error && <div className="mt-4 rounded-xl border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-300">{error}</div>}
        {notice && <div className="mt-4 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-300">{notice}</div>}
        <div data-tour="faq-list" className="mt-5 space-y-3">
          {loading ? <div className="flex justify-center py-16 text-[var(--tg-text-muted)]"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading FAQs...</div> : filteredItems.map((item) => (
            <article key={item.id} className="rounded-xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface-raised)] p-4">
              <div className="flex items-start justify-between gap-3"><div className="min-w-0"><h3 className="font-semibold text-[var(--tg-text-primary)]">{item.question}</h3><p className="mt-2 whitespace-pre-wrap text-sm text-[var(--tg-text-secondary)]">{item.answer}</p></div><div className="flex shrink-0"><Button variant="ghost" size="icon" onClick={() => startEdit(item)} aria-label="Edit FAQ"><Pencil className="h-4 w-4" /></Button><Button variant="ghost" size="icon" onClick={() => remove(item)} disabled={working} aria-label="Delete FAQ"><Trash2 className="h-4 w-4 text-rose-400" /></Button></div></div>
              {(item.metadata?.type || item.metadata?.source || item.metadata?.tags) && <div className="mt-3 flex flex-wrap gap-1.5 text-xs text-[var(--tg-text-muted)]">{item.metadata?.type && <span className="rounded-full bg-indigo-500/10 px-2 py-1">{item.metadata.type}</span>}{item.metadata?.source && <span className="rounded-full bg-[var(--tg-surface)] px-2 py-1">Source: {item.metadata.source}</span>}{(Array.isArray(item.metadata?.tags) ? item.metadata.tags : []).map((tag) => <span key={tag} className="rounded-full bg-[var(--tg-surface)] px-2 py-1">#{tag}</span>)}</div>}
            </article>
          ))}
          {!loading && !filteredItems.length && <div className="rounded-xl border border-dashed border-[var(--tg-border-soft)] py-14 text-center text-sm text-[var(--tg-text-muted)]">{search ? 'No FAQs match your search.' : 'No FAQs have been added yet.'}</div>}
        </div>
      </section>

      <section className="h-fit space-y-4 rounded-2xl border border-[var(--tg-border-soft)] bg-[var(--tg-surface)] p-5 sm:p-6 lg:sticky lg:top-4">
        <div className="flex items-center justify-between"><div><h2 className="text-lg font-semibold">{form.id ? 'Edit FAQ' : 'Create FAQ'}</h2><p className="text-xs text-[var(--tg-text-muted)]">Saved directly to Meta Agent Knowledge.</p></div>{form.id && <Button variant="ghost" size="icon" onClick={() => setForm(emptyForm)} aria-label="Cancel editing"><X className="h-4 w-4" /></Button>}</div>
        <label data-tour="faq-question" className="block space-y-2"><span className="text-sm font-medium">Question</span><textarea rows={3} className={textAreaClass} value={form.question} onChange={(event) => setForm((current) => ({ ...current, question: event.target.value }))} placeholder="What are your operating hours?" /></label>
        <label data-tour="faq-answer" className="block space-y-2"><span className="text-sm font-medium">Answer</span><textarea rows={7} className={textAreaClass} value={form.answer} onChange={(event) => setForm((current) => ({ ...current, answer: event.target.value }))} placeholder="We are available Monday to Friday..." /></label>
        <div data-tour="faq-metadata" className="space-y-3"><div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2"><label className="space-y-2"><span className="text-xs font-medium">Type (optional)</span><Input value={form.type} onChange={(event) => setForm((current) => ({ ...current, type: event.target.value }))} placeholder="hours" /></label><label className="space-y-2"><span className="text-xs font-medium">Source (optional)</span><Input value={form.source} onChange={(event) => setForm((current) => ({ ...current, source: event.target.value }))} placeholder="Operations team" /></label></div><label className="block space-y-2"><span className="text-xs font-medium">Tags (comma separated)</span><Input value={form.tags} onChange={(event) => setForm((current) => ({ ...current, tags: event.target.value }))} placeholder="hours, support, Nagpur" /></label></div>
        <Button data-tour="faq-save" className="w-full" onClick={save} disabled={working || !form.question.trim() || !form.answer.trim()}>{working ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}{form.id ? 'Save changes' : 'Create FAQ'}</Button>
      </section>
    </div>
  );
};

export default FaqKnowledgePanel;
